import { useMemo, useState } from 'react'
import { AlertTriangle, ArrowRight, CheckCircle2, FileSpreadsheet, Upload, X } from 'lucide-react'
import { Board, BoardColumn, entryColumn } from '../../lib/boards'
import { parseCsv } from '../../lib/csv'
import {
  ImportError,
  ImportResult,
  ImportSettings,
  MAX_IMPORT_ROWS,
  buildImportPlan,
  fetchImportedRefs,
  guessSettings,
  guessStageMap,
  ignoredHeaders,
  importCards,
  importedFieldLabels,
  stageValues,
} from '../../lib/boardImport'
import { TutorOption, inputClass, labelClass } from './CardFieldInput'
import { ghostButton, iconButton, overlayClass, panelClass, primaryButton, secondaryButton } from './ui'

type CsvFile = { name: string; headers: string[]; rows: string[][] }

const plural = (n: number, one: string, many: string = `${one}s`) => `${n} ${n === 1 ? one : many}`

export function ImportCardsModal({
  boards,
  columns,
  tutors,
  initialBoardId,
  actorEmail,
  onClose,
}: {
  boards: Board[]
  columns: BoardColumn[]
  tutors: TutorOption[]
  initialBoardId: string
  actorEmail: string | null
  onClose: (importedInto: Board | null) => void
}) {
  const [file, setFile] = useState<CsvFile | null>(null)
  const [boardId, setBoardId] = useState(initialBoardId)
  const [settings, setSettings] = useState<ImportSettings | null>(null)
  const [importedRefs, setImportedRefs] = useState<Set<string> | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [savedAny, setSavedAny] = useState(false)
  const [result, setResult] = useState<ImportResult | null>(null)
  const [includeImported, setIncludeImported] = useState(false)

  const board = boards.find(b => b.id === boardId)
  const columnsFor = (id: string) => columns.filter(c => c.boardId === id).sort((a, b) => a.sortOrder - b.sortOrder)
  const boardColumns = columnsFor(boardId)

  const plan = useMemo(
    () => (file && settings && importedRefs ? buildImportPlan(file.rows, settings, { tutors, importedRefs, includeImported }) : []),
    [file, settings, importedRefs, tutors, includeImported]
  )
  const toImport = plan.filter(r => r.status === 'new')
  // Rows whose card is already on a board: skipped, or added again as new cards when ticked.
  const importedBefore = plan.filter(r => r.importedBefore && (r.status === 'duplicate' || r.status === 'new')).length
  const repeated = plan.filter(r => r.status === 'repeated').length
  const skipped = plan.filter(r => r.status === 'skipped_stage').length
  const extras = [
    [toImport.reduce((n, r) => n + r.notes.length, 0), 'note'],
    [toImport.reduce((n, r) => n + r.tasks.length, 0), 'task or call', 'tasks or calls'],
    [toImport.reduce((n, r) => n + r.history.length, 0), 'history entry', 'history entries'],
  ] as const

  const close = () => {
    if (!busy) onClose(savedAny ? board ?? null : null)
  }

  const loadImportedRefs = async () => {
    try {
      setImportedRefs(await fetchImportedRefs())
    } catch (e: any) {
      setError(`Could not check for cards imported before: ${e?.message || 'unknown error'}`)
    }
  }

  const readFile = async (picked: File) => {
    setError(null)
    if (!/\.(csv|txt)$/i.test(picked.name) && picked.type !== 'text/csv') {
      setError('Choose a .csv file. Save Excel sheets as "CSV UTF-8" first.')
      return
    }
    try {
      const parsed = parseCsv(await picked.text())
      if (parsed.length < 2) {
        setError('The file needs a header row and at least one row of data.')
        return
      }
      const [head, ...rows] = parsed
      if (rows.length > MAX_IMPORT_ROWS) {
        setError(`This file has ${rows.length.toLocaleString()} rows. Import at most ${MAX_IMPORT_ROWS.toLocaleString()} at a time.`)
        return
      }
      const headers = head.map(h => h.trim())
      const guessed = guessSettings(headers)
      // Rows without a stage, and stages that match no column, go to New enquiry.
      const first = entryColumn(boardColumns)?.id ?? ''
      setIncludeImported(false)
      setFile({ name: picked.name, headers, rows })
      setSettings({ ...guessed, defaultColumnId: first, stageMap: guessStageMap(rows, guessed.stageColumn, boardColumns, first) })
      if (guessed.idColumn == null) {
        setImportedRefs(new Set())
      } else {
        setImportedRefs(null)
        await loadImportedRefs()
      }
    } catch (e: any) {
      setError(e?.message || 'Could not read the file.')
    }
  }

  const changeBoard = (id: string) => {
    const cols = columnsFor(id)
    const first = entryColumn(cols)?.id ?? ''
    setBoardId(id)
    setSettings(s => s && { ...s, defaultColumnId: first, stageMap: guessStageMap(file?.rows ?? [], s.stageColumn, cols, first) })
  }

  const runImport = async () => {
    if (!board || !file || toImport.length === 0) return
    setBusy(true)
    setError(null)
    setProgress(0)
    try {
      setResult(await importCards(board.id, toImport, { fileName: file.name, actorEmail }, setProgress))
      setSavedAny(true)
    } catch (e: any) {
      const saved = e instanceof ImportError ? e.imported : 0
      if (saved > 0) {
        setSavedAny(true)
        await loadImportedRefs()
      }
      const retry = settings?.idColumn != null
        ? 'Press Import again to add the rest.'
        : 'Those are on the board, so importing the file again would add them twice.'
      setError(saved > 0 ? `Stopped after ${saved} of ${toImport.length} cards: ${e?.message}. ${retry}` : e?.message || 'Could not import the cards.')
    } finally {
      setBusy(false)
    }
  }

  const fileInput = (
    <input
      type="file"
      accept=".csv,text/csv"
      className="sr-only"
      onChange={(e) => {
        const picked = e.target.files?.[0]
        if (picked) readFile(picked)
        e.target.value = ''
      }}
    />
  )

  const columnSelect = (value: string | null, onChange: (id: string | null) => void, label: string, allowSkip: boolean) => (
    <select aria-label={label} value={value ?? ''} onChange={(e) => onChange(e.target.value || null)} className={inputClass}>
      {boardColumns.map(c => (
        <option key={c.id} value={c.id}>
          {c.name}
        </option>
      ))}
      {allowSkip && <option value="">Don't import</option>}
    </select>
  )

  return (
    <div className={`${overlayClass} z-50`}>
      <div className={`${panelClass} w-full max-w-lg max-h-[90vh] flex flex-col`} role="dialog" aria-modal="true" aria-label="Import cards">
        <div className="flex-shrink-0 flex items-start justify-between gap-4 p-6 pb-5">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 flex-shrink-0 rounded-xl bg-brand-400/10 text-brand-600 dark:text-brand-400 flex items-center justify-center">
              <Upload className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Import cards</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Add cards to a board from a CSV file.</p>
            </div>
          </div>
          <button onClick={close} disabled={busy} aria-label="Close" className={iconButton}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 space-y-5">
          {result ? (
            <div className="flex flex-col items-center text-center gap-3 py-6">
              <CheckCircle2 className="h-10 w-10 text-green-500" />
              <p className="text-base font-semibold text-gray-900 dark:text-white">
                Added {plural(result.imported, 'card')} to the {board?.name} board
              </p>
              {result.detailsFailed > 0 && (
                <p className="text-sm text-amber-600 dark:text-amber-400 flex items-start gap-1.5 text-left">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  Some notes, tasks or calls couldn't be saved on {plural(result.detailsFailed, 'card')}.
                </p>
              )}
              {result.historyFailed > 0 && (
                <p className="text-sm text-amber-600 dark:text-amber-400 flex items-start gap-1.5 text-left">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  {result.historyFailed === 1 ? "1 card wasn't" : `${result.historyFailed} cards weren't`} marked as imported, so
                  importing this file again would add {result.historyFailed === 1 ? 'it' : 'them'} twice.
                </p>
              )}
            </div>
          ) : !file || !settings ? (
            <label
              onDragOver={(e) => {
                e.preventDefault()
                setDragOver(true)
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault()
                setDragOver(false)
                const picked = e.dataTransfer.files[0]
                if (picked) readFile(picked)
              }}
              className={`flex flex-col items-center justify-center gap-2 h-40 rounded-xl border-2 border-dashed cursor-pointer transition-colors ${
                dragOver ? 'border-brand-400 bg-brand-400/[0.06]' : 'border-gray-300 dark:border-white/10 hover:border-brand-400/60'
              }`}
            >
              <FileSpreadsheet className="h-8 w-8 text-gray-400" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Choose a CSV file or drop it here</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">The first row must be the column names.</span>
              {fileInput}
            </label>
          ) : (
            <>
              <div className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 dark:border-white/10">
                <FileSpreadsheet className="h-5 w-5 flex-shrink-0 text-gray-400" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{file.name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{plural(file.rows.length, 'row')}</p>
                </div>
                <label className={`${secondaryButton} h-8 px-3 cursor-pointer`}>
                  Change
                  {fileInput}
                </label>
              </div>

              <div>
                <label htmlFor="import-board" className={labelClass}>
                  Add to
                </label>
                <select id="import-board" value={boardId} onChange={(e) => changeBoard(e.target.value)} className={inputClass}>
                  {boards.map(b => (
                    <option key={b.id} value={b.id}>
                      {b.name} board
                    </option>
                  ))}
                </select>
              </div>

              {settings.stageColumn == null ? (
                <div>
                  <p className={labelClass}>Put the cards in</p>
                  {columnSelect(settings.defaultColumnId, (id) => id && setSettings({ ...settings, defaultColumnId: id }), 'Column for the cards', false)}
                </div>
              ) : (
                <div>
                  <p className={labelClass}>Where each stage goes</p>
                  <div className="space-y-2">
                    {stageValues(file.rows, settings.stageColumn).map(({ value, count }) => (
                      <div key={value} className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
                        <span className="text-sm text-gray-800 dark:text-gray-100 truncate" title={value}>
                          {value || <em className="text-gray-400">(blank)</em>}{' '}
                          <span className="text-xs text-gray-400 tabular-nums">({count})</span>
                        </span>
                        <ArrowRight className="h-4 w-4 text-gray-400" />
                        {columnSelect(
                          settings.stageMap[value] ?? null,
                          (id) => setSettings({ ...settings, stageMap: { ...settings.stageMap, [value]: id } }),
                          `Board column for ${value || 'blank stage'}`,
                          true
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {importedBefore > 0 && (
                <label className="flex items-start gap-2.5 p-3 rounded-xl border border-gray-200 dark:border-white/10 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeImported}
                    onChange={(e) => setIncludeImported(e.target.checked)}
                    className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-gray-300 dark:border-white/20 text-brand-600 focus:ring-brand-400"
                  />
                  <span>
                    <span className="font-medium text-gray-900 dark:text-white">Import cards that were imported before</span>
                    <span className="block mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                      {importedBefore === 1
                        ? '1 row in this file matches a card already on a board. Tick this to add it again as a new card. The card already there stays.'
                        : `${importedBefore} rows in this file match cards already on a board. Tick this to add them again as new cards. The cards already there stay.`}
                    </span>
                  </span>
                </label>
              )}

              <div className="p-3 rounded-xl bg-gray-50 dark:bg-white/[0.03] text-sm space-y-1">
                <p className="font-medium text-gray-900 dark:text-white">
                  {importedRefs ? `${plural(toImport.length, 'card')} will be added` : 'Checking…'}
                </p>
                {importedBefore > 0 && (
                  <p className="text-gray-600 dark:text-gray-300">
                    {includeImported
                      ? `${plural(importedBefore, 'row')} imported before will be added again.`
                      : `${plural(importedBefore, 'row')} already imported, skipped.`}
                  </p>
                )}
                {repeated > 0 && (
                  <p className="text-gray-600 dark:text-gray-300">
                    {plural(repeated, 'row')} {repeated === 1 ? 'repeats an ID' : 'repeat IDs'} from earlier in the file, skipped.
                  </p>
                )}
                {skipped > 0 && <p className="text-gray-600 dark:text-gray-300">{plural(skipped, 'row')} set to "Don't import".</p>}
                {extras.some(([n]) => n > 0) && (
                  <p className="text-gray-600 dark:text-gray-300">
                    With{' '}
                    {extras
                      .filter(([n]) => n > 0)
                      .map(([n, one, many]) => plural(n, one, many))
                      .join(', ')}
                    , each keeping its original date.
                  </p>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 pt-1">
                  Fills in: {importedFieldLabels(settings.mapping).join(', ') || 'nothing (no column names match card fields)'}
                </p>
                {ignoredHeaders(file.headers, settings).length > 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">Not used: {ignoredHeaders(file.headers, settings).join(', ')}</p>
                )}
              </div>
            </>
          )}
        </div>

        {error && (
          <p className="flex-shrink-0 px-6 pt-4 text-sm text-red-500 flex items-start gap-1.5" role="alert">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" /> {error}
          </p>
        )}

        <div className="flex-shrink-0 flex justify-end gap-2 mt-6 px-6 py-4 border-t border-gray-100 dark:border-white/[0.06] bg-gray-50/80 dark:bg-white/[0.02] rounded-b-2xl">
          {result ? (
            <button onClick={close} className={primaryButton}>
              Done
            </button>
          ) : (
            <>
              <button onClick={close} disabled={busy} className={ghostButton}>
                Cancel
              </button>
              <button onClick={runImport} disabled={busy || !importedRefs || toImport.length === 0} className={primaryButton}>
                {busy ? `Importing ${progress} of ${toImport.length}…` : `Import ${plural(toImport.length, 'card')}`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
